import Config from "./config.js";

/**
 * The 1Mind launcher script itself: build the URL, inject it once, and report when the
 * experience has mounted.
 *
 * NATIVE COPY — see the note in Config.js. Behaviour is identical to
 * legacy/src2/1mind/Launcher.js; only the syntax is modernised.
 *
 * 1Mind always mounts into an element with the id in Config.mountId. That element is also
 * what decides EMBED vs WIDGET: verified 2026-08-28, when it exists the launcher renders
 * inline into it even for a widget deployment with data-display_mode="widget" set. So
 * creating the mount before injecting the script is what pins the embedded experience in place.
 */
export const Launcher = {
  /**
   * The injected script element — also the run-once guard. One launcher per page: a second
   * would open a second 1Mind session.
   */
  script: null,

  /** Poll handle for the mount watcher. */
  timer: null,

  /**
   * Resolve a launcher URL for a use case / environment pair.
   *
   * @param  {string} useCase  embedded | widget | behindTheForm
   * @param  {string} env      sandbox | production
   * @return {string}
   */
  url(useCase, env) {
    const deployment = Config.deployments[useCase];

    if (!deployment) {
      throw new Error(`[1mind] unknown use case "${useCase}"`);
    }

    if (env !== "sandbox" && env !== "production") {
      throw new Error(`[1mind] env must be "sandbox" or "production", got "${env}"`);
    }

    return Config.launcherBase + deployment[env];
  },

  /**
   * Is 1Mind already running on this page? True once any code — this bundle, the connect
   * bar's AI Advisor panel, or a hand-written section — has injected the launcher or
   * created the mount.
   *
   * 1Mind mounts into a single fixed element id, so a page can only host ONE experience.
   * This is a DOM check on purpose: each bundle carries its own copy of this module, so
   * module-level state cannot see across them. That is doubly true now that native and
   * legacy each have their own copy.
   *
   * @return {boolean}
   */
  claimed() {
    if (document.querySelector(`script[src^="${Config.launcherBase}"]`)) return true;

    return !!document.getElementById(Config.mountId);
  },

  /**
   * Ensure the mount element exists inside the caller's container. 1Mind owns the id, so
   * the container is whatever the page provides and the mount is created inside it.
   *
   * @param  {string|Element} container  Selector or element
   * @return {Element|null}  The mount, or null when the container isn't on the page
   */
  mount(container) {
    const host = typeof container === "string" ? document.querySelector(container) : container;

    if (!host) return null;

    const existing = document.getElementById(Config.mountId);

    if (existing) return existing;

    const mount = document.createElement("div");

    mount.id = Config.mountId;
    mount.className = "onemind-mount";

    host.appendChild(mount);

    return mount;
  },

  /**
   * Inject the launcher script. Idempotent — later calls return the element injected by
   * the first.
   *
   * @param  {object} options  { useCase, env, attributes }
   * @return {Element}
   */
  inject({ useCase, env, attributes = {} } = {}) {
    if (this.script) return this.script;

    // Something else already loaded a launcher (the connect bar's AI Advisor panel, or
    // another section on the page) — adopt it rather than opening a second session.
    const existing = document.querySelector(`script[src^="${Config.launcherBase}"]`);

    if (existing) {
      this.script = existing;

      return existing;
    }

    const script = document.createElement("script");

    script.defer = true;
    script.src = this.url(useCase, env);

    Object.entries(attributes).forEach(([key, value]) => {
      // Values reach 1Mind as strings; skip empties so an absent form field doesn't
      // overwrite context 1Mind already has from the HubSpot cookie.
      if (value === null || value === undefined || value === "") return;

      script.setAttribute(key, String(value));
    });

    document.head.appendChild(script);

    this.script = script;

    return script;
  },

  /**
   * Wait for the experience to mount. The launcher fetches the deployment before
   * rendering, so there is no load event to hang a spinner on.
   *
   * @param  {function} onReady          Called with the mount element
   * @param  {function} [onUnavailable]  Called when it never mounts
   */
  whenReady(onReady, onUnavailable) {
    clearInterval(this.timer);

    let attempts = 0;

    this.timer = setInterval(() => {
      const mount = document.getElementById(Config.mountId);

      if (mount && mount.querySelector("iframe")) {
        clearInterval(this.timer);

        if (typeof onReady === "function") onReady(mount);

        return;
      }

      if (++attempts >= 50) {
        // ~15s
        clearInterval(this.timer);

        if (typeof onUnavailable === "function") onUnavailable();
      }
    }, 300);
  },
};

export default Launcher;
